import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabaseClient'
import { generateWhatsAppLink, generatePropertyMessage, generateLeadMessage, generateAppointmentMessage } from '../lib/whatsappService'

function WhatsAppButton({ type, data, phoneNumber = '234', buttonText = null, session, companyName }) {
  const [showModal, setShowModal] = useState(false)
  const [customMessage, setCustomMessage] = useState('')
  const [multipleNumbers, setMultipleNumbers] = useState('')
  const [defaultCompanyName, setDefaultCompanyName] = useState(companyName || 'EstatesFlow')

  // Fetch company name if not provided
  useEffect(() => {
    if (!companyName && session?.user?.company_id) {
      const fetchCompanyName = async () => {
        const { data: company } = await supabase
          .from('companies')
          .select('name')
          .eq('id', session.user.company_id)
          .single()
        if (company?.name) {
          setDefaultCompanyName(company.name)
        }
      }
      fetchCompanyName()
    }
  }, [companyName, session])

  const getDefaultMessage = () => {
    const company = defaultCompanyName
    
    if (type === 'property' && data) return generatePropertyMessage(data, company)
    if (type === 'lead' && data) return generateLeadMessage(data, data.status, company)
    if (type === 'appointment' && data) return generateAppointmentMessage(data, data.client, company)
    if (type === 'client' && data) return `👤 *${company} - Client Profile*\n\nName: ${data.full_name}\nEmail: ${data.email}\nPhone: ${data.phone}\nType: ${data.client_type}\n\nNeed assistance? Reply to this message.`
    if (type === 'invoice' && data) return `💰 *${company} - Invoice Reminder*\n\nInvoice: ${data.invoice_number}\nAmount: ${data.currency} ${data.total}\nDue Date: ${new Date(data.due_date).toLocaleDateString()}\n\nThank you for your business!`
    if (type === 'contract' && data) return `⚖️ *${company} - Contract Signing Reminder*\n\nContract: ${data.title}\nType: ${data.type}\nStatus: ${data.status}\n\nPlease review and sign at your earliest convenience.`
    if (type === 'interview' && data) return `🎯 *${company} - Interview Reminder*\n\nYour interview is scheduled for ${new Date(data.scheduled_date).toLocaleString()}\nType: ${data.interview_type}\n\nWe look forward to meeting you!`
    if (type === 'offer' && data) return `📄 *${company} - Job Offer*\n\nPosition: ${data.job_postings?.title}\nSalary: ${data.salary_currency} ${data.salary_offered?.toLocaleString()}\nStart Date: ${new Date(data.start_date).toLocaleDateString()}\n\nCongratulations! We're excited to have you join our team.`
    if (type === 'complaint' && data) return `⚠️ *${company} - Complaint Update*\n\nType: ${data.complaint_type}\nStatus: ${data.status}\nPriority: ${data.priority}\n\nWe're working on resolving your issue. Thank you for your patience.`
    if (type === 'feedback' && data) return `⭐ *${company} - Thank you for your feedback!*\n\nRating: ${data.rating}/5\nCategory: ${data.category}\n\nWe appreciate your input and will use it to improve our services.`
    if (type === 'bulk' && data) return data.message
    if (type === 'agent' && data) return `👋 Hello ${data.email},\n\nThis is a message from ${company} Leadership.\n\nThank you for your dedication!`
    if (type === 'salary' && data) return `💰 *${company} - Salary Advice*\n\nPeriod: ${data.month}/${data.year}\nAmount: ${data.currency} ${data.net_salary?.toLocaleString()}\nStatus: ${data.status}\n\nPlease check your account for details.`
    if (type === 'tax' && data) return `🏛️ *${company} - Tax Return*\n\nType: ${data.tax_type}\nPeriod: ${data.period}\nAmount Due: ${data.currency} ${data.amount_due?.toLocaleString()}\nDue Date: ${new Date(data.due_date).toLocaleDateString()}\n\nPlease ensure timely payment.`
    return `🏢 *${company}*\n\nThank you for being a valued customer!\n\nVisit our portal for more information.`
  }

  const getButtonText = () => {
    if (buttonText) return buttonText
    switch(type) {
      case 'property': return '📱 Share on WhatsApp'
      case 'lead': return '📱 Send Lead Update'
      case 'appointment': return '📱 Send Reminder'
      case 'client': return '📱 Message Client'
      case 'invoice': return '📱 Payment Reminder'
      case 'contract': return '📱 Sign Reminder'
      case 'interview': return '📱 Interview Reminder'
      case 'offer': return '📱 Share Offer'
      case 'complaint': return '📱 Complaint Update'
      case 'feedback': return '📱 Feedback Request'
      case 'bulk': return '📱 Message Team'
      case 'agent': return '📱 WhatsApp'
      default: return '📱 WhatsApp'
    }
  }

  const logToDatabase = async (number, message) => {
    if (!session?.user?.id) return
    await supabase.from('whatsapp_messages').insert({
      to_number: number,
      message: message,
      sent_by: session.user.id,
      type: type,
      reference_id: data?.id,
      company_name: defaultCompanyName
    })
  }

  const handleSend = async () => {
    const inputNumber = document.getElementById('singleNumber')?.value || phoneNumber
    const message = customMessage || getDefaultMessage()
    if (!inputNumber) {
      alert('Please enter a phone number')
      return
    }
    
    await logToDatabase(inputNumber, message)
    const link = generateWhatsAppLink(inputNumber, message)
    window.open(link, '_blank')
    setShowModal(false)
  }

  const handleSendMultiple = async () => {
    const numbers = multipleNumbers.split(',').map(n => n.trim()).filter(n => n)
    if (numbers.length === 0) {
      alert('Please enter at least one phone number')
      return
    }
    const message = customMessage || getDefaultMessage()
    
    for (const number of numbers) {
      await logToDatabase(number, message)
      const link = generateWhatsAppLink(number, message)
      window.open(link, '_blank')
    }
    setShowModal(false)
  }

  const getModalTitle = () => {
    switch(type) {
      case 'property': return `Share Property on WhatsApp - ${defaultCompanyName}`
      case 'lead': return `Send Lead Update - ${defaultCompanyName}`
      case 'appointment': return `Send Appointment Reminder - ${defaultCompanyName}`
      case 'invoice': return `Send Payment Reminder - ${defaultCompanyName}`
      case 'contract': return `Send Contract Reminder - ${defaultCompanyName}`
      case 'interview': return `Send Interview Reminder - ${defaultCompanyName}`
      case 'bulk': return `Message All - ${defaultCompanyName}`
      default: return `Send WhatsApp Message - ${defaultCompanyName}`
    }
  }

  return (
    <>
      <button onClick={() => setShowModal(true)} style={whatsappBtn}>
        {getButtonText()}
      </button>

      {showModal && (
        <div style={modalOverlay} onClick={() => setShowModal(false)}>
          <div style={whatsappModal} onClick={(e) => e.stopPropagation()}>
            <h3>{getModalTitle()}</h3>
            
            <div style={{marginBottom: 15}}>
              <label>Message (edit if needed):</label>
              <textarea
                rows="4"
                value={customMessage || getDefaultMessage()}
                onChange={(e) => setCustomMessage(e.target.value)}
                style={inputStyle}
              />
            </div>

            <div style={{marginBottom: 15}}>
              <label>Send to:</label>
              <input
                type="text"
                id="singleNumber"
                placeholder="Phone number (e.g., 2348012345678)"
                defaultValue={phoneNumber}
                style={inputStyle}
              />
              <button onClick={handleSend} style={sendBtn}>
                Send Now
              </button>
            </div>

            <hr style={{margin: '15px 0'}} />
            <div>
              <label>Or send to multiple:</label>
              <input
                type="text"
                placeholder="2348012345678, 2348098765432"
                value={multipleNumbers}
                onChange={(e) => setMultipleNumbers(e.target.value)}
                style={inputStyle}
              />
              <button onClick={handleSendMultiple} style={sendBtn}>
                Send to All
              </button>
            </div>

            <button onClick={() => setShowModal(false)} style={closeBtn}>Close</button>
          </div>
        </div>
      )}
    </>
  )
}

// Styles remain the same...
const whatsappBtn = {
  background: '#075E54',
  color: '#fff',
  border: 'none',
  padding: '10px 18px',
  borderRadius: 8,
  cursor: 'pointer',
  fontSize: 13,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6
}

const sendBtn = {
  background: '#075E54',
  color: '#fff',
  border: 'none',
  padding: '10px 20px',
  borderRadius: 8,
  cursor: 'pointer',
  marginTop: 8,
  width: '100%',
  fontWeight: 600
}

const whatsappModal = {
  background: '#fff',
  padding: 25,
  borderRadius: 20,
  width: '90%',
  maxWidth: 450,
  maxHeight: '90vh',
  overflow: 'auto'
}

const modalOverlay = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0,0,0,0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000
}

const inputStyle = {
  width: '100%',
  padding: 10,
  margin: '8px 0',
  border: '1px solid #cbd5e1',
  borderRadius: 10,
  fontSize: 14,
  fontFamily: 'inherit'
}

const closeBtn = {
  background: '#6b7280',
  color: '#fff',
  border: 'none',
  padding: '10px 16px',
  borderRadius: 8,
  cursor: 'pointer',
  width: '100%',
  marginTop: 15
}

export default WhatsAppButton